import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginSuccess } from "../store/authSlice";
import api from "../utils/api";
import { API_ENDPOINTS, ROUTES } from "../config/constants";
import { normalizeAuthResponse } from "../utils/authResponse";
import { fetchOrganizationContext } from "../store/organizationContextSlice";
import { Link } from "react-router-dom";
import { Loader2, Lock, LogIn, Mail, Sparkles } from "lucide-react";
import PasswordInput from "../components/PasswordInput";

const validate = (email, password) => {
    const errors = {};
    if (!email.trim()) errors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        errors.email = "Invalid email address.";
    if (!password) errors.password = "Password is required.";
    else if (password.length < 6)
        errors.password = "Password must be at least 6 characters.";
    return errors;
};

const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [error, setError] = useState("");
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleBlur = (field) => {
        const errors = validate(email, password);
        setFieldErrors((prev) => ({ ...prev, [field]: errors[field] }));
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (isLoggingIn) return;

        setError("");

        const errors = validate(email, password);
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setIsLoggingIn(true);

        // hit backend end point for login with essential crediantials
        try {
            const response = await api.post(API_ENDPOINTS.LOGIN, {
                email,
                password,
            });

            // converted the response in simple form
            const authPayload = normalizeAuthResponse(response.data);

            if (!authPayload) {
                setError("User not found. Please check your email.");
                return;
            }

            // stored this data into redux and local storage
            dispatch(loginSuccess(authPayload));
            dispatch(fetchOrganizationContext());
            navigate(ROUTES.DASHBOARD);
        } catch (err) {
            if (err.response?.status === 401) {
                setError("Invalid email or password");
            } else if (err.response?.status === 422) {
                setError("Please fill all required fields");
            } else {
                setError("An error occurred during login");
            }
            console.error(err);
        } finally {
            setIsLoggingIn(false);
        }
    };

    return (
        <div className="flex min-h-[calc(100vh-64px)] items-center justify-center bg-[#F8F8FE] px-3 py-8 sm:px-6 lg:px-8">
            <div className="grid w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm lg:grid-cols-[0.95fr_1.05fr]">
                <div className="hidden bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 p-8 text-white lg:flex lg:flex-col lg:justify-between">
                    <div>
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
                            <Sparkles size={24} aria-hidden="true" />
                        </div>
                        <h1 className="mt-8 text-3xl font-bold leading-tight">
                            Welcome back to OptiMind
                        </h1>
                        <p className="mt-4 max-w-sm text-sm leading-6 text-indigo-50">
                            Continue your lead search, product planning, and AI
                            outreach workflows from one focused workspace.
                        </p>
                    </div>
                    <p className="text-xs font-medium uppercase tracking-[0.2em] text-indigo-100">
                        AI-powered B2B outreach
                    </p>
                </div>

                <div className="w-full p-5 sm:p-8 lg:p-10">
                    <div className="mx-auto max-w-md">
                        <div className="mb-6 text-center sm:mb-8">
                            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 lg:hidden">
                                <Sparkles size={24} aria-hidden="true" />
                            </div>
                            <p className="text-sm font-medium text-indigo-600">
                                Sign in to your workspace
                            </p>
                            <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
                                Log into OptiMind
                            </h2>
                        </div>

                        {error && (
                            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                                {error}
                            </div>
                        )}

                        <form
                            onSubmit={handleLogin}
                            noValidate
                            className="space-y-4"
                        >
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                                    Email ID
                                </label>
                                <div className="relative">
                                    <Mail
                                        size={18}
                                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                        aria-hidden="true"
                                    />
                                    <input
                                        type="email"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (fieldErrors.email)
                                                setFieldErrors((prev) => ({
                                                    ...prev,
                                                    email: undefined,
                                                }));

                                            setError("");
                                        }}
                                        onBlur={() => handleBlur("email")}
                                        className={`w-full rounded-xl border bg-white px-10 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 ${
                                            fieldErrors.email
                                                ? "border-red-400"
                                                : "border-slate-200"
                                        }`}
                                    />
                                </div>
                                {fieldErrors.email && (
                                    <p className="mt-1.5 text-xs text-red-600">
                                        {fieldErrors.email}
                                    </p>
                                )}
                            </div>

                            <div>
                                <div className="mb-1.5 flex items-center justify-between gap-3">
                                    <label className="block text-sm font-medium text-slate-700">
                                        Password
                                    </label>
                                    <Link
                                        to={ROUTES.FORGOT_PASSWORD}
                                        className="text-xs font-semibold text-indigo-600 transition hover:text-indigo-700"
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                                <PasswordInput
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        if (fieldErrors.password)
                                            setFieldErrors((prev) => ({
                                                ...prev,
                                                password: undefined,
                                            }));
                                        setError("");
                                    }}
                                    onBlur={() => handleBlur("password")}
                                    hasError={Boolean(fieldErrors.password)}
                                    leadingIcon={Lock}
                                />
                                {fieldErrors.password && (
                                    <p className="mt-1.5 text-xs text-red-600">
                                        {fieldErrors.password}
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={isLoggingIn}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-400"
                            >
                                {isLoggingIn ? (
                                    <Loader2
                                        size={18}
                                        className="animate-spin"
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <LogIn size={18} aria-hidden="true" />
                                )}
                                {isLoggingIn ? "Logging in..." : "Log In"}
                            </button>
                            <p className="text-center text-sm text-slate-600">
                                Don't have an account?{" "}
                                <Link
                                    to="/signup"
                                    className="font-semibold text-indigo-600 transition hover:text-indigo-700"
                                >
                                    Sign up
                                </Link>
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
